import { isDemo } from './branding.js';
import { DemoLogo, DemoSpinner } from './DemoLogo.js';

import styles from './BrandLogo.module.css';

/**
 * The Planet Schwarz brand lockup (S03: "Planet Schwarz logo (scalable SVG) in
 * the login card and sidebar, theme-aware").
 *
 * Ported from the approved mock's `BrandLogo.jsx`. Inline SVG rather than an
 * asset file for two reasons the mock's own comment gives: it inherits the gold
 * theme colour through `currentColor`, so it follows the token rather than
 * needing a second file per theme, and it stays crisp at any size.
 *
 * The mark is drawn as a mask: the planet body and ring are painted, then the
 * two ring gaps and the twin sparkles are *carved out* of it. That is why
 * everything sits in one `<rect fill="currentColor">` — the shape is the hole,
 * not the ink.
 *
 * Geometry measured from the client’s artwork rather than eyeballed: disc r 33
 * at (50, 36), ring 50 × 10 and dead level, both centred. An earlier version was
 * traced by hand from a PNG and got the construction wrong as well as the
 * numbers — it drew a lightning bolt through the planet, when the S is really
 * the ring seen through the disc.
 */

/**
 * A four-pointed sparkle as a closed quadratic path.
 *
 * `c` controls how tightly the curve pinches towards the centre: a smaller
 * fraction of the radius gives sharper points.
 */
function sparkle(cx: number, cy: number, r: number): string {
  const c = 0.18 * r;
  return (
    `M${cx},${cy - r} Q${cx + c},${cy - c} ${cx + r},${cy}` +
    ` Q${cx + c},${cy + c} ${cx},${cy + r}` +
    ` Q${cx - c},${cy + c} ${cx - r},${cy}` +
    ` Q${cx - c},${cy - c} ${cx},${cy - r} Z`
  );
}

/**
 * The E of PLANET, which the brand draws without its stem — three bars and no
 * upright.
 *
 * A glyph rather than a font trick: no weight of Poppins has a stemless E, and
 * masking the upright off the real letter would depend on the font rendering at
 * exactly the metrics we assumed. Drawn, it is the same shape at every size.
 *
 * All three bars are identical — same length, same weight, evenly spaced. A
 * typeface shortens an E's middle arm, and drawing it that way here looks right
 * until you hold it against the artwork, where the three are plainly equal.
 *
 * Sized in `em` so it tracks the three lockup sizes without three sets of
 * numbers, and baseline-aligned so it sits on the line with its neighbours
 * rather than near it. `currentColor` keeps it on the same token as the rest of
 * the word.
 */
function StemlessE(): JSX.Element {
  return (
    <svg className={styles.stemlessE} viewBox="0 0 88 110" aria-hidden="true" focusable="false">
      <rect x="5" y="5" width="78" height="12" />
      <rect x="5" y="49" width="78" height="12" />
      <rect x="5" y="93" width="78" height="12" />
    </svg>
  );
}

/**
 * `maskId` exists because two lockups render at once (login card and sidebar)
 * and duplicate SVG ids would make one of them mask against the other's shape.
 */
export function PlanetMark({
  className,
  maskId = 'ps-mark-cut',
}: {
  className?: string | undefined;
  maskId?: string | undefined;
}): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 100 72" fill="none" aria-hidden="true">
      <mask id={maskId} maskUnits="userSpaceOnUse">
        <rect width="100" height="72" fill="#000" />
        {/* planet body + ring, in white = visible gold */}
        <ellipse className={styles.ring} cx="50" cy="36" rx="50" ry="10" fill="#fff" />
        <circle cx="50" cy="36" r="33" fill="#fff" />
        {/* The two wedges that make the "S".
         *
         * There is no lightning bolt in this mark, which is what the earlier
         * drawing got wrong. The S is the *ring*, read through the disc: where
         * the ring crosses the planet a thin dark gap separates the two, and
         * because that gap appears above the centre on the right and below it on
         * the left, the eye joins them into an S. Carve the gaps and the letter
         * appears on its own.
         *
         * Each wedge is widest where the ring leaves the disc's edge and tapers
         * to nothing a little past the middle, which is why they are filled
         * outlines rather than strokes — a stroke has one width for its whole
         * length and could not close to a point.
         *
         * Both wide ends deliberately overshoot the disc. A closed path has to
         * join its last point back to its first, and where that join lands on
         * the disc it draws as a straight line across it — which is exactly the
         * stray line the right-hand wedge showed when it stopped at x=80.5 and
         * the disc edge was at 81.1. Ending past the silhouette leaves the join
         * with nothing to mark. Do not pull these back to the edge to tidy them.
         *
         * They carry `.ringGap` so they thin with the ring during the sign-in
         * animation. A gap that stayed put while the ring behind it narrowed
         * would slide off onto the planet. */}
        <path
          className={styles.ringGap}
          d="M16.3,43.6 C26,44.6 38,45.7 46.4,46.1 C39,47.5 27,49.1 17.6,50 Z"
          fill="#000"
        />
        <path
          className={styles.ringGap}
          d="M84,28.2 C76,27.6 64,26.6 53.3,25.9 C62,24.6 72,23.4 84,21.9 Z"
          fill="#000"
        />
        {/* And the two slivers out on the wings, at the corners the wedges do not
         * reach: upper-left and lower-right.
         *
         * Each runs from the ring's edge to a point near the disc's widest part,
         * about 1.6 units across. They are what sells the ring passing *behind*
         * the planet on one side and in front of it on the other — without them
         * the wing simply merges into the disc on that side and the depth goes
         * flat. Easy to miss at 40px and obvious the moment the mark is
         * enlarged, which is how they came to be left out of the first pass.
         *
         * Their mouths overshoot the ring's edge for the same reason the wedges
         * overshoot the disc: a closed path joins its last point back to its
         * first, and wherever that join still has ring under it, it draws as a
         * line straight across the cut. Ending outside leaves it nothing to
         * mark. Do not trim these back to the edge. */}
        <path
          className={styles.ringGap}
          d="M17.09,27.8 C17.03,30.8 16.95,33.8 16.85,36.4 C16.1,34.2 15.2,31 14.22,27.8 Z"
          fill="#000"
        />
        <path
          className={styles.ringGap}
          d="M82.83,44.2 C82.87,41.5 83.1,38.7 83.3,36 C84,38.6 84.9,41.5 85.77,44.2 Z"
          fill="#000"
        />
        {/* Twin sparkles carved into the lower-left of the planet.
         *
         * These two occasionally glint — see `.sparkle` in the stylesheet. They
         * are holes in the gold, so "brighter" here means *less* carved: the
         * shape swells and fades, gold floods back through it, and it settles.
         * At rest they are exactly the mock's shapes, untouched. */}
        <path className={styles.sparkle} d={sparkle(45.8, 54.6, 3.2)} fill="#000" />
        <path
          className={`${styles.sparkle} ${styles.sparkleLate}`}
          d={sparkle(37, 59, 2.15)}
          fill="#000"
        />
      </mask>
      <rect width="100" height="72" fill="currentColor" mask={`url(#${maskId})`} />
    </svg>
  );
}

export function BrandLogo({
  size = 'default',
  maskId,
  busy = false,
}: {
  size?: 'default' | 'sidebar' | 'big';
  maskId?: string | undefined;
  /** Signing in: the ring turns and the sparkles catch, until the page leaves. */
  busy?: boolean;
}): JSX.Element {
  // Demo mode replaces the lockup wholesale — see branding.ts. `busy` has no
  // counterpart there and is dropped rather than approximated: the Sirma artwork
  // is still, and a lockup that flickered on sign-in would read worse than one
  // that simply does not react.
  if (isDemo) return <DemoLogo size={size} />;

  return (
    <div
      className={[
        styles.logo,
        size === 'default' ? '' : (styles[size] ?? ''),
        busy ? styles.busy : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <PlanetMark className={styles.mark} maskId={maskId} />
      <WordMark />
    </div>
  );
}

/**
 * The mark alone, always working — a spinner in the product's own vocabulary.
 *
 * Reuses `.busy` rather than defining a second animation, so a page waiting for
 * data turns its ring exactly as the login card and the sidebar do. Three states
 * that mean "wait" should not be three different motions.
 *
 * No word mark: this sits inline beside a line of text, where "PLANET SCHWARZ"
 * next to "Loading agents…" would read as a heading rather than an indicator.
 */
export function PlanetSpinner({ maskId }: { maskId: string }): JSX.Element {
  // The one mark demo mode cannot simply re-skin: this component *is* the
  // motion, and the Sirma lockup has no geometry to turn.
  if (isDemo) return <DemoSpinner />;

  return (
    <span className={`${styles.logo} ${styles.busy} ${styles.spinner}`}>
      <PlanetMark className={styles.mark} maskId={maskId} />
    </span>
  );
}

/**
 * Just the words.
 *
 * Separable because on a phone the lockup is split in two: the mark is the menu
 * toggle floating over the drawer, and the words sit in the drawer beside it. The
 * typography stays here so there is one definition of it; where it goes and when
 * it appears is the caller's business, hence `className`.
 */
export function WordMark({
  size = 'default',
  className,
}: {
  /** Matches BrandLogo, so a split lockup can be reassembled at one size. */
  size?: 'default' | 'sidebar' | 'big';
  className?: string | undefined;
}): JSX.Element {
  // The Sirma artwork is one lockup with the name already in it, so there is no
  // mark to separate the words from: the mobile drawer shows the whole thing,
  // which is what the split was reassembling anyway. `className` still has to
  // travel with it — it is what puts this in the drawer and keeps it off the
  // desktop layout, where the full lockup is already on screen.
  if (isDemo) return <DemoLogo size={size} className={className} />;

  return (
    <div
      className={[styles.word, size === 'default' ? '' : (styles[size] ?? ''), className]
        .filter(Boolean)
        .join(' ')}
    >
      <span className={styles.planet}>
        PLAN
        <StemlessE />
        {/* The real letter, for anything that reads rather than looks: screen
            readers announce PLANET, and selecting the lockup copies "PLANET"
            rather than "PLANT". */}
        <span className="sr-only">E</span>T
      </span>
      <span className={styles.schwarz}>SCHWARZ</span>
    </div>
  );
}
